/**
 * הקמת לקוח — the four provisioning steps. OA-0001 §3.9.
 *
 *   > *"A SaaS product cannot be considered operational if it cannot admit its first customer …
 *   > the first administrator must not require direct database editing. Do not solve initial
 *   > provisioning through an undocumented SQL insert."*
 *
 * The four steps, and they are the four routes in `apps/api/src/routes/provisioning.ts` — one form
 * per route, in the order the server requires, because step 2 cannot be posted until step 1 has
 * returned the tenant it belongs to:
 *
 *   1. הקמת הלקוח                platform.tenant.create
 *   2. פתיחת מנוי                platform.subscription.create
 *   3. מודולים ומכסות שנרכשו     platform.subscription.sku.add
 *   4. הזמנת מנהל המשרד הראשון   platform.tenant.activate
 *
 * ── WHY THIS IS A SCREEN AND NOT A MODAL ────────────────────────────────────────────────────────
 *
 * 09 §2.1 M3: a modal is ONE DECISION, not a workflow. This is four writes against four routes with
 * a value carried between them; it has an address, and losing the page loses real server state that
 * has to be findable again — which is M5's test failed outright.
 *
 * ── AND WHY EACH STEP POSTS WHEN IT IS COMPLETED ────────────────────────────────────────────────
 *
 * Not at the end, in a batch. A write is NEVER retried by `api/client.ts` — the client cannot know
 * whether the server committed before the connection died — so a four-write batch that failed
 * halfway would leave an operator unable to say which half happened. One step, one write, one
 * visible outcome. Steps 5-11 belong to the CUSTOMER and appear nowhere on this screen: GL-POINT
 * creates the account and the commercial terms, and then stops (OA-0001 §3.2).
 *
 * ── WHAT HAPPENS WITH NO API ────────────────────────────────────────────────────────────────────
 *
 * The forms are wired to `api/client.ts`, which is the only place this app talks to the server. With
 * no server they fail, and the screen SAYS the request did not reach anything. It does not advance,
 * and it does not invent a tenant uid — a wizard that congratulated an operator on a customer that
 * does not exist is the exact defect the honesty rule of this milestone is written against.
 */

import { useState } from 'react';
import type { FormEvent } from 'react';
import { AppShell } from '../shell/AppShell';
import { useWorkContext } from '../shell/ContextProvider';
import { DenialNotice } from '../components/DenialNotice';
import { ErrorState, StateBlock } from '../components/StateBlock';
import { SurfaceNotice } from '../components/SurfaceNotice';
import { ApiError, DeniedError } from '../api/client';
import { callOperation } from '../api/operations';
import type { DenyBody } from '../api/contract';
import { routes } from '../routes';

const TITLE_HE = 'הקמת לקוח חדש';

/** The four steps, named as the operator experiences them. Each maps to exactly one route. */
const STEPS_HE: readonly string[] = [
  'הקמת הלקוח',
  'פתיחת מנוי',
  'מודולים ומכסות שנרכשו',
  'הזמנת מנהל המשרד הראשון',
];

/**
 * 02 §5.2 — the customer kinds. Reporting only: `kind` is on the entitlement resolver's FORBIDDEN
 * INPUT list (CF-66), so it records what sort of customer this is and can never reach a decision.
 */
const TENANT_KINDS: readonly { readonly code: string; readonly labelHe: string }[] = [
  { code: 'cpa_firm', labelHe: 'משרד רואי חשבון' },
  { code: 'direct_business', labelHe: 'עסק ישיר' },
  { code: 'internal', labelHe: 'פנימי' },
];

const PERIOD_KINDS: readonly { readonly code: string; readonly labelHe: string }[] = [
  { code: 'month', labelHe: 'חודשי' },
  { code: 'year', labelHe: 'שנתי' },
];

/** Progress is a value the server handed back, never a counter the screen incremented for itself. */
type Progress =
  | { readonly step: 1 }
  | { readonly step: 2; readonly tenantUid: string }
  | { readonly step: 3; readonly tenantUid: string }
  | { readonly step: 4; readonly tenantUid: string }
  | { readonly step: 'done'; readonly tenantUid: string; readonly invitationUid: string };

type Failure =
  | { readonly kind: 'denied'; readonly body: DenyBody }
  | { readonly kind: 'error'; readonly requestUid: string }
  | { readonly kind: 'unreachable' };

function stepNumber(progress: Progress): number {
  return progress.step === 'done' ? STEPS_HE.length + 1 : progress.step;
}

export function PlatformTenantProvisioningScreen(): JSX.Element {
  const ctx = useWorkContext();

  const [progress, setProgress] = useState<Progress>({ step: 1 });
  const [pending, setPending] = useState(false);
  const [failure, setFailure] = useState<Failure | null>(null);

  const [legalName, setLegalName] = useState('');
  const [slug, setSlug] = useState('');
  const [kind, setKind] = useState('cpa_firm');
  const [planCode, setPlanCode] = useState('');
  const [periodKind, setPeriodKind] = useState('year');
  const [skuCode, setSkuCode] = useState('');
  const [quantity, setQuantity] = useState('1');
  const [adminName, setAdminName] = useState('');
  const [adminEmail, setAdminEmail] = useState('');

  if (ctx.surface !== 'PLATFORM') {
    return <SurfaceNotice titleHe={TITLE_HE} requires="PLATFORM" actual={ctx.surface} />;
  }

  /**
   * One write, one outcome. `post` never retries — see the binding retry policy in `api/client.ts` —
   * so a transport failure here is reported and the step is NOT advanced.
   */
  async function submitStep(run: () => Promise<Progress>): Promise<void> {
    setPending(true);
    setFailure(null);
    try {
      setProgress(await run());
    } catch (err: unknown) {
      if (err instanceof DeniedError) {
        setFailure({ kind: 'denied', body: err.body });
      } else if (err instanceof ApiError && err.status === 0) {
        setFailure({ kind: 'unreachable' });
      } else if (err instanceof ApiError) {
        setFailure({ kind: 'error', requestUid: err.requestUid });
      } else {
        setFailure({ kind: 'unreachable' });
      }
    } finally {
      setPending(false);
    }
  }

  function onCreateTenant(e: FormEvent): void {
    e.preventDefault();
    void submitStep(async () => {
      const created = await callOperation('platform.tenant.create', {
        body: { slug, legalName, kind },
      });
      return { step: 2, tenantUid: created.tenantUid };
    });
  }

  function onCreateSubscription(e: FormEvent, tenantUid: string): void {
    e.preventDefault();
    void submitStep(async () => {
      await callOperation('platform.subscription.create', {
        params: { tenant_uid: tenantUid },
        body: { planCode, periodKind, trialEndsAt: null },
      });
      return { step: 3, tenantUid };
    });
  }

  function onAddSku(e: FormEvent, tenantUid: string): void {
    e.preventDefault();
    void submitStep(async () => {
      await callOperation('platform.subscription.sku.add', {
        params: { tenant_uid: tenantUid },
        body: { skuCode, quantity: Number.parseInt(quantity, 10) },
      });
      return { step: 4, tenantUid };
    });
  }

  function onInviteAdministrator(e: FormEvent, tenantUid: string): void {
    e.preventDefault();
    void submitStep(async () => {
      const invited = await callOperation('platform.tenant.activate', {
        params: { tenant_uid: tenantUid },
        body: { email: adminEmail, displayName: adminName },
      });
      return { step: 'done', tenantUid, invitationUid: invited.invitationUid };
    });
  }

  const current = stepNumber(progress);

  return (
    <AppShell
      titleHe={TITLE_HE}
      backToParent={{ labelHe: 'הלקוחות', href: routes.platformTenants }}
      provenanceHe="כל שלב נשלח לשרת בנפרד. פעולת כתיבה אינה נשלחת שוב אוטומטית, ולכן שלב שנכשל נשאר פתוח."
    >
      {/* The four steps, always all four, with the current one named. A wizard that hides the
          remaining steps is a wizard whose length the operator discovers one screen at a time. */}
      <ol className="steps">
        {STEPS_HE.map((label, index) => (
          <li
            key={label}
            className="steps__item"
            data-state={index + 1 < current ? 'done' : index + 1 === current ? 'current' : 'pending'}
          >
            <span className="steps__index" dir="ltr">
              {index + 1}
            </span>
            <span className="steps__label">{label}</span>
          </li>
        ))}
      </ol>

      {failure === null ? null : failure.kind === 'denied' ? (
        <DenialNotice deny={failure.body} />
      ) : failure.kind === 'error' ? (
        <ErrorState requestUid={failure.requestUid} />
      ) : (
        <StateBlock
          titleHe="הבקשה לא הגיעה לשרת"
          bodyHe="לא הצלחנו ליצור קשר עם השרת, ולכן השלב הזה לא בוצע."
          tone="error"
          // A write is never retried automatically, so the retry is the operator's explicit act.
          nextStep={<p>אפשר לשלוח את השלב שוב לאחר שהשרת יהיה זמין. שום דבר לא נשמר בינתיים.</p>}
        />
      )}

      {progress.step === 1 ? (
        <form className="form" onSubmit={onCreateTenant}>
          <h2 className="form__title">שלב 1 — הקמת הלקוח</h2>
          <p className="form__lead">
            השלב הזה יוצר את רשומת הלקוח בלבד. הוא אינו מעניק דבר עד שהמנוי נפתח ומופעל.
          </p>

          <label className="field">
            <span className="field__label">השם המשפטי של הלקוח</span>
            <input
              className="field__input"
              value={legalName}
              onChange={(e) => setLegalName(e.currentTarget.value)}
              required
            />
          </label>

          <label className="field">
            <span className="field__label">מזהה ההקמה</span>
            <input
              className="field__input"
              dir="ltr"
              value={slug}
              onChange={(e) => setSlug(e.currentTarget.value)}
              required
            />
            <span className="field__hint">מזהה טכני קבוע. אינו מוצג ללקוח ואינו מזהה עסקי.</span>
          </label>

          <fieldset className="field field--choice">
            <legend className="field__label">סוג הלקוח</legend>
            {TENANT_KINDS.map((k) => (
              <label key={k.code} className="choice">
                <input
                  type="radio"
                  name="tenantKind"
                  value={k.code}
                  checked={kind === k.code}
                  onChange={() => setKind(k.code)}
                />
                <span>{k.labelHe}</span>
              </label>
            ))}
            <span className="field__hint">
              נרשם לצורכי דיווח בלבד. סוג הלקוח אינו משפיע על הרשאות ואינו נכנס לחישוב הזכאות.
            </span>
          </fieldset>

          <div className="actions-row">
            <button type="submit" className="btn btn--primary" disabled={pending}>
              {pending ? 'שולח…' : 'הקמת הלקוח'}
            </button>
          </div>
        </form>
      ) : null}

      {progress.step === 2 ? (
        <form className="form" onSubmit={(e) => onCreateSubscription(e, progress.tenantUid)}>
          <h2 className="form__title">שלב 2 — פתיחת מנוי</h2>
          <p className="form__lead">המנוי נפתח כטיוטה. הוא אינו מעניק גישה עד שמצבו משתנה לפעיל.</p>

          <label className="field">
            <span className="field__label">קוד התכנית</span>
            <input
              className="field__input"
              dir="ltr"
              value={planCode}
              onChange={(e) => setPlanCode(e.currentTarget.value)}
              required
            />
          </label>

          <fieldset className="field field--choice">
            <legend className="field__label">מחזור החיוב</legend>
            {PERIOD_KINDS.map((p) => (
              <label key={p.code} className="choice">
                <input
                  type="radio"
                  name="periodKind"
                  value={p.code}
                  checked={periodKind === p.code}
                  onChange={() => setPeriodKind(p.code)}
                />
                <span>{p.labelHe}</span>
              </label>
            ))}
          </fieldset>

          <div className="actions-row">
            <button type="submit" className="btn btn--primary" disabled={pending}>
              {pending ? 'שולח…' : 'פתיחת מנוי'}
            </button>
          </div>
        </form>
      ) : null}

      {progress.step === 3 ? (
        <form className="form" onSubmit={(e) => onAddSku(e, progress.tenantUid)}>
          <h2 className="form__title">שלב 3 — מודולים ומכסות שנרכשו</h2>
          <p className="form__lead">
            הוספת מוצר למנוי מעדכנת את גרסת הזכאות, ולכן היא נכנסת לתוקף בבקשה הבאה של הלקוח.
          </p>

          <label className="field">
            <span className="field__label">קוד המוצר</span>
            <input
              className="field__input"
              dir="ltr"
              value={skuCode}
              onChange={(e) => setSkuCode(e.currentTarget.value)}
              required
            />
          </label>

          <label className="field">
            <span className="field__label">כמות שנרכשה</span>
            <input
              className="field__input"
              type="number"
              dir="ltr"
              min={1}
              value={quantity}
              onChange={(e) => setQuantity(e.currentTarget.value)}
              required
            />
            <span className="field__hint">
              הכמות היא המכסה שהלקוח רכש. מכסה מלאה חוסמת הוספה בלבד, ולעולם אינה מוחקת נתונים קיימים.
            </span>
          </label>

          <div className="actions-row">
            <button type="submit" className="btn btn--primary" disabled={pending}>
              {pending ? 'שולח…' : 'הוספה למנוי'}
            </button>
          </div>
        </form>
      ) : null}

      {progress.step === 4 ? (
        <form className="form" onSubmit={(e) => onInviteAdministrator(e, progress.tenantUid)}>
          <h2 className="form__title">שלב 4 — הזמנת מנהל המשרד הראשון</h2>
          <p className="form__lead">
            נשלחת הזמנה, ולא נוצר חשבון עם סיסמה. מנהל המשרד קובע את הסיסמה שלו בעצמו, והמערכת אינה
            מחזיקה אותה בשום שלב.
          </p>

          <label className="field">
            <span className="field__label">שם מנהל המשרד</span>
            <input
              className="field__input"
              value={adminName}
              onChange={(e) => setAdminName(e.currentTarget.value)}
              required
            />
          </label>

          <label className="field">
            <span className="field__label">דואר אלקטרוני</span>
            <input
              className="field__input"
              type="email"
              dir="ltr"
              value={adminEmail}
              onChange={(e) => setAdminEmail(e.currentTarget.value)}
              required
            />
          </label>

          <div className="actions-row">
            <button type="submit" className="btn btn--primary" disabled={pending}>
              {pending ? 'שולח…' : 'שליחת ההזמנה'}
            </button>
          </div>
        </form>
      ) : null}

      {progress.step === 'done' ? (
        <StateBlock
          titleHe="הלקוח הוקם וההזמנה נשלחה"
          bodyHe="מכאן ואילך המשך ההקמה שייך ללקוח: מנהל המשרד מצטרף, מזמין משתמשים, מקים חברות ומעניק גישה — ללא מעורבות של הפלטפורמה."
          nextStep={
            <div className="actions-row">
              <a className="btn btn--primary" href={routes.platformTenant(progress.tenantUid)}>
                פתיחת כרטיס הלקוח
              </a>
              <a className="btn" href={routes.platformTenants}>
                חזרה לרשימת הלקוחות
              </a>
            </div>
          }
        />
      ) : null}
    </AppShell>
  );
}
